import type { BatchTransactionProps } from '@api/hooks/useBatch.ts';
import { Chip, Typography } from '@gurupay/gp-ui-library';
import { formatNumberAmount } from '@utils/utils.ts';
import type { TFunction } from 'i18next';
import { TRANSACTION_STATUS } from 'types/layout.ts';
import { BatchStatus } from 'types/operations.ts';

import { DeleteTransaction } from './DeleteTransaction.tsx';

const deletableBatchStatuses = [BatchStatus.UNSIGNED, BatchStatus.UNSIGNED_WITH_WARNINGS];

const statusColorConfig = {
	failed: 'error',
	signed: 'success',
	executed: 'success',
	created: 'warning',
	unsigned: 'warning',
	initializing: 'neutral',
	processed: 'warning',
};

const vopStatusConfig = {
	MATCH: 'success',
	NO_MATCH: 'error',
	CLOSE_MATCH: 'warning',
	CANNOT_VERIFY: 'neutral',
};

export const transactionRenderers = (
	t: TFunction,
	isDeleteDisabled: boolean,
	batchStatus: BatchStatus,
) => ({
	beneficiary: ({ beneficiary }: BatchTransactionProps) => (
		<div className="flex flex-col">
			<Typography className="font-bold" level="small-text">
				{beneficiary.name}
			</Typography>
			<Typography className="font-medium text-neutral-600" level="small-text">
				{beneficiary.account_number}
			</Typography>
		</div>
	),

	vop: ({ vopStatus }: BatchTransactionProps) => (
		<Chip
			color={vopStatusConfig[vopStatus] ?? 'neutral'}
			label={vopStatus ? t(`vopStatus.${vopStatus.toLowerCase()}`) : 'N/A'}
			size="medium"
		/>
	),

	description: ({ payment_details }: BatchTransactionProps) => (
		<Typography className="font-medium break-all w-[100px] block" level="small-text">
			{payment_details}
		</Typography>
	),

	status: ({ status }: BatchTransactionProps) => (
		<Chip
			color={statusColorConfig[status]}
			label={t(`paymentStatus.${status.toLowerCase()}`)}
			size="medium"
		/>
	),

	operation: ({ payment_type }: BatchTransactionProps) => (
		<Typography className="font-medium capitalize text-neutral-800" level="small-text">
			{t(`operationType.${payment_type.toLowerCase()}`)}
		</Typography>
	),

	amount: ({ amount }: BatchTransactionProps) => (
		<Typography className="font-bold" level="small-text">
			{formatNumberAmount(amount.toFixed(2))}
		</Typography>
	),

	delete: ({ id, beneficiary, status }: BatchTransactionProps) =>
		status === TRANSACTION_STATUS.CREATED &&
		deletableBatchStatuses.includes(batchStatus) ? (
			<DeleteTransaction
				disabled={isDeleteDisabled}
				fileName={beneficiary.name}
				id={id}
			/>
		) : null,
});
